import { auth } from './firebase.js';
import {
    signInWithPopup,
    GoogleAuthProvider,
    signOut as firebaseSignOut,
    onAuthStateChanged,
} from 'firebase/auth';

// Shared auth + payment-method Vuex slice for the letter-composer apps
// (congress, letters). Both stores carried an identical auth/card slice; only
// the payment-method callables and a post-sign-out reset differed. This factory
// returns { state, getters, mutations, actions } to spread into createStore({}).
//
// Inject the app's payment callables and (optionally) an onSignOut hook:
//   const authSlice = createAuthSlice({
//     checkPaymentMethodApi: checkCongressPaymentMethod,
//     createSetupIntentApi: createCongressSetupIntent,
//     saveBillingAddressApi: saveBillingAddress,
//     onSignOut: ({ commit }) => commit('RESET_LETTER'),
//   });
//   createStore({ state: { ...authSlice.state, ...appState }, getters: { ...authSlice.getters, ... }, ... });
//
// The slice's signOut dispatches 'clearDraft' (each app still defines that
// action) and calls onSignOut after clearing user + card state.
export function createAuthSlice({
    checkPaymentMethodApi,
    createSetupIntentApi,
    saveBillingAddressApi,
    onSignOut,
} = {}) {
    return {
        state: {
            user: null,
            isAuthenticated: false,
            authLoading: true,
            hasCardOnFile: false,
            cardLast4: null,
            cardBrand: null,
            billingAddress: null,
            cardLoading: false,
        },

        getters: {
            isLoggedIn: (state) => state.isAuthenticated && state.user !== null,
            needsCard: (state) => !state.hasCardOnFile,
        },

        mutations: {
            SET_USER(state, user) {
                state.user = user;
                state.isAuthenticated = !!user;
            },
            SET_AUTH_LOADING(state, loading) {
                state.authLoading = loading;
            },
            SET_CARD_INFO(state, { hasCard, last4, brand, billingAddress }) {
                state.hasCardOnFile = hasCard;
                state.cardLast4 = last4 || null;
                state.cardBrand = brand || null;
                state.billingAddress = billingAddress || null;
            },
            SET_CARD_LOADING(state, loading) {
                state.cardLoading = loading;
            },
        },

        actions: {
            initAuth({ commit, dispatch }) {
                return new Promise((resolve) => {
                    onAuthStateChanged(auth, async (user) => {
                        if (user) {
                            commit('SET_USER', {
                                uid: user.uid,
                                email: user.email,
                                displayName: user.displayName,
                                photoURL: user.photoURL,
                            });
                            await dispatch('checkPaymentMethod');
                        } else {
                            commit('SET_USER', null);
                            commit('SET_CARD_INFO', { hasCard: false });
                        }
                        commit('SET_AUTH_LOADING', false);
                        resolve(user);
                    });
                });
            },

            async signInWithGoogle({ commit, dispatch }) {
                const provider = new GoogleAuthProvider();
                try {
                    const result = await signInWithPopup(auth, provider);
                    commit('SET_USER', {
                        uid: result.user.uid,
                        email: result.user.email,
                        displayName: result.user.displayName,
                        photoURL: result.user.photoURL,
                    });
                    await dispatch('checkPaymentMethod');
                    return result.user;
                } catch (error) {
                    console.error('Sign in error:', error);
                    throw error;
                }
            },

            async signOut({ commit, dispatch }) {
                // Clear sensitive data from session storage before logout
                dispatch('clearDraft');
                await firebaseSignOut(auth);
                commit('SET_USER', null);
                commit('SET_CARD_INFO', { hasCard: false });
                if (onSignOut) onSignOut({ commit, dispatch });
            },

            async checkPaymentMethod({ commit }) {
                try {
                    const result = await checkPaymentMethodApi({});
                    commit('SET_CARD_INFO', {
                        hasCard: result.data.hasPaymentMethod,
                        last4: result.data.cardLast4,
                        brand: result.data.cardBrand,
                        billingAddress: result.data.billingAddress,
                    });
                    return result.data;
                } catch (error) {
                    console.error('Error checking payment method:', error);
                    commit('SET_CARD_INFO', { hasCard: false });
                    return { hasPaymentMethod: false };
                }
            },

            async createSetupIntent() {
                try {
                    const result = await createSetupIntentApi({});
                    return result.data; // { clientSecret, customerId }
                } catch (error) {
                    console.error('Error creating setup intent:', error);
                    throw error;
                }
            },

            async saveBillingAddress({ dispatch }, paymentMethodId) {
                try {
                    await saveBillingAddressApi({ paymentMethodId });
                    // Refresh payment method info to get card details
                    await dispatch('checkPaymentMethod');
                    return { success: true };
                } catch (error) {
                    console.error('Error saving billing address:', error);
                    throw error;
                }
            },
        },
    };
}
